import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {setQueryClient} from '@tnc/shared';
import {useEffect, useState} from 'react';

/**
 * This component provides the TanStack (React) Query Client to the application. It is used to
 * manage the cache and data fetching for the application. We are using state to ensure that the
 * QueryClient is only created once.
 * @param {React.ReactNode} children the children to render
 * @returns {React.ReactNode} the children
 */
export const ReactQueryClientProvider = ({children}: {children: React.ReactNode}) => {
  // Using useState here to ensure that the QueryClient is only created once
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            retryOnMount: false,
            // 30 minutes on prod, 0 seconds on dev unless specified in .env (VITE_APP_CACHE_TIME)
            staleTime:
              import.meta.env.VITE_APP_MODE === 'development'
                ? Number(import.meta.env.VITE_APP_CACHE_TIME ?? 0)
                : 1000 * 60 * 30,
            // keepPreviousData: true was deprecated in v3.5.0
            // Using the below placeholderData function to achieve the same effect
            // https://github.com/TanStack/query/discussions/6460
            placeholderData: (prev: unknown) => prev,
          },
        },
      })
  );

  // Set the global QueryClient as early as possible
  // This ensures shared hooks can access it even outside React components
  useEffect(() => {
    setQueryClient(queryClient);
  }, [queryClient]);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};
